import React, {useState} from 'react';
import PropTypes from 'prop-types';

import '../../styles/datatable.scss';

import DebouncedInput from './DebouncedInput';
import Header from './Header';
import ColumnFooter from './ColumnFooter';
import Paginator from './Paginator';
import TableFooter from './TableFooter';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  getFilteredRowModel,
  getFacetedRowModel,
  getPaginationRowModel,
  getFacetedUniqueValues,
  getFacetedMinMaxValues,
} from '@tanstack/react-table';
import {fuzzyFilter} from 'libs/datatable';

const TabularDataBasic = ({
  data,
  columns,
  filterAllColumns,
  showColumnFooters,
  showPagination,
  tableHeight,
  tableWidth,
  maxHeight,
  optionalFooterBody,
  getCellClass,
  getGroupBorderStyle,
  collapseData,
  noTableFooter,
  enableRowSelection,
  onRowSelectionChange,
  rowSelection,
  initialPageSize,
}) => {
  const [sorting, setSorting] = useState([]);

  const [columnFilters, setColumnFilters] = useState([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const finalData = collapseData ? collapseData(data) : data;

  const table = useReactTable({
    data: finalData,
    columns: columns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    initialState: {
      pagination: {
        pageSize: initialPageSize,
      },
    },
    state: {
      sorting,
      columnFilters,
      globalFilter,
      rowSelection,
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: showPagination ? getPaginationRowModel() : '',
    onGlobalFilterChange: setGlobalFilter,
    globalFilterFn: fuzzyFilter,
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getFacetedMinMaxValues: getFacetedMinMaxValues(),
    enableColumnResizing: false,
    enablePinning: false,
    enableRowSelection,
    onRowSelectionChange,
  });

  const bodyStyle = {};
  const tableStyle = {};
  if (maxHeight) {
    bodyStyle.maxHeight = maxHeight;
    bodyStyle.display = 'block';
  }
  if (tableHeight) {
    bodyStyle.height = tableHeight;
  }
  if (tableWidth) {
    tableStyle.width = tableWidth;
  }

  return (
    <div className={'TabularDataBasic'}>
      <div className={'p-2'} >
        {filterAllColumns &&
          <div className={'filter-container pull-right'}>
            <label style={{paddingRight: '1em'}}>Search</label>
            <DebouncedInput
              value={globalFilter ? globalFilter : ''}
              onChange={(value) => setGlobalFilter(String(value))}
              className='p-2 font-lg shadow border border-block'
              placeholder='Search all columns...'
            />
          </div>
        }
        <div className='h-2' />
        <table
          className={'table table-striped'}
          style={tableStyle}
        >
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return <Header key={header.id} properties={header} table={table}/>;
                })}
              </tr>
            ))}
          </thead>
          <tbody style={bodyStyle}>
            {table.getRowModel().rows.map((row) => {
              let cellClass = getCellClass ? getCellClass(row) : '';
              return (
                <tr
                  key={row.id}
                >
                  {row.getVisibleCells().map((cell) => {
                    let borderStyle = getGroupBorderStyle ? getGroupBorderStyle(cell) : '';
                    return (
                      <td
                        key={cell.id}
                        style={{
                          display: cell.getContext().column.getSize() == 0 ? 'none' : '',
                          textAlign: cell.column.columnDef.textAlign ? cell.column.columnDef.textAlign : '',
                          ...borderStyle,
                          width: isNaN(cell.getContext().column.getSize()) ? 0 : cell.getContext().column.getSize(),
                        }}
                        className={cellClass ? cellClass : ''}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
          {showColumnFooters &&
            <ColumnFooter footerGroups={table.getFooterGroups()} />
          }
        </table>
        {showPagination &&
          <Paginator
            setPageIndex={table.setPageIndex}
            getCanPreviousPage={table.getCanPreviousPage}
            getCanNextPage={table.getCanNextPage}
            previousPage={table.previousPage}
            nextPage={table.nextPage}
            getPageCount={table.getPageCount}
            setPageSize={table.setPageSize}
            currentPageIndex={table.getState().pagination.pageIndex}
            currentPageSize={table.getState().pagination.pageSize}
            rowCount={table.getPrePaginationRowModel().rows.length}
          />
        }
        {(optionalFooterBody || (!showPagination && !noTableFooter)) &&
          <TableFooter
            optionalFooterBody={optionalFooterBody}
            rowCount={table.getPrePaginationRowModel().rows.length}
            totalCount={data.length}
          />
        }
      </div>
    </div>
  );
};

TabularDataBasic.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  filterAllColumns: PropTypes.bool,
  showColumnFooters: PropTypes.bool,
  showPagination: PropTypes.bool,
  tableHeight: PropTypes.string,
  tableWidth: PropTypes.string,
  maxHeight: PropTypes.string,
  optionalFooterBody: PropTypes.element,
  getCellClass: PropTypes.func,
  getGroupBorderStyle: PropTypes.func,
  collapseData: PropTypes.func,
  noTableFooter: PropTypes.bool,
  enableRowSelection: PropTypes.bool,
  onRowSelectionChange: PropTypes.func,
  rowSelection: PropTypes.any,
  initialPageSize: PropTypes.number,
};

TabularDataBasic.defaultProps = {
  filterAllColumns: true,
  showColumnFooters: false,
  showPagination: true,
  tableHeight: '100%',
  tableWidth: '100%',
  maxHeight: '100%',
  noTableFooter: false,
  enableRowSelection: false,
  onRowSelectionChange: () => {},
  rowSelection: [],
  initialPageSize: 10,
};

export default TabularDataBasic;
